import { AppShell } from "@client/components/layout/AppShell";
import { ErrorBox, Spinner } from "@client/components/ui";
import { authQuery } from "@client/lib/queries";
import { ToastProvider } from "@client/lib/toast";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, redirect, useLocation } from "@tanstack/react-router";

export type RouterContext = {
  readonly queryClient: QueryClient;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    let authenticated = false;
    try {
      const me = await context.queryClient.ensureQueryData(authQuery());
      authenticated = me.authenticated;
    } catch {
      // No API reachable, or the request failed — treat as unauthenticated rather than
      // blocking on a blank screen.
      authenticated = false;
    }
    if (!authenticated && location.pathname !== "/login") throw redirect({ to: "/login" });
    if (authenticated && location.pathname === "/login") throw redirect({ to: "/" });
  },
  component: RootComponent,
  errorComponent: ({ error }) => (
    <div className="flex min-h-screen items-center justify-center bg-app p-6">
      <ErrorBox error={error} />
    </div>
  ),
  pendingComponent: () => (
    <div className="flex min-h-screen items-center justify-center bg-app text-fg-3">
      <Spinner size={28} />
    </div>
  ),
});

function RootComponent() {
  const location = useLocation();
  const isLogin = location.pathname === "/login";
  return (
    <ToastProvider>
      {isLogin ? (
        <Outlet />
      ) : (
        <AppShell>
          <Outlet />
        </AppShell>
      )}
    </ToastProvider>
  );
}
